import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import useDebounce from '../../../hooks/useDebounce';
import {
  Box,
  Button,
  TextField,
  InputAdornment,
  Alert,
  Snackbar,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
} from '@mui/material';
import { Add, Search, Download, Cloud, History, Warning, SwapHoriz } from '@mui/icons-material';
import PageHeader from '../../../components/PageHeader';
import ProceedingTable from '../components/ProceedingTable';
import ProceedingModalForm from '../components/ProceedingModalForm';
import LoadingLogo from '../../../components/LoadingLogo';
import OneDriveBulkImport from '../components/OneDriveBulkImport';
import BulkImportProgress from '../components/BulkImportProgress';
import ActiveJobsBadge from '../components/ActiveJobsBadge';
import proceedingService from '../services/proceedingService';
import { usePermissions } from '../../../hooks/usePermissions';
import { useAuth } from '../../../hooks/useAuth';
import { companyService } from '../../companies';
import { retentionService } from '../../retentions';

const ProceedingList = () => {
  const { hasPermission } = usePermissions();
  const { user, isOwner } = useAuth();
  const navigate = useNavigate();
  const [proceedings, setProceedings] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [retentions, setRetentions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 2000);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');
  const [openModal, setOpenModal] = useState(false);
  const [selectedProceeding, setSelectedProceeding] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [oneDriveDialogOpen, setOneDriveDialogOpen] = useState(false);
  const [oneDriveMode, setOneDriveMode] = useState('new');
  const [activeJob, setActiveJob] = useState(null);
  const [progressDialogOpen, setProgressDialogOpen] = useState(false);
  const [retentionAlert, setRetentionAlert] = useState('');

  useEffect(() => {
    loadCompanies();
  }, []);

  useEffect(() => {
    if (selectedCompany) {
      loadRetentions(selectedCompany);
    } else {
      setRetentions([]);
    }
  }, [selectedCompany]);

  useEffect(() => {
    if (user?.companyId && !selectedCompany) {
      setSelectedCompany(user.companyId.toString());
    }
  }, [user?.companyId]);

  useEffect(() => {
    loadProceedings();
  }, [page, debouncedSearch, selectedCompany, startDate, endDate, sortBy, sortDir, retentionAlert]);

  const loadCompanies = async () => {
    try {
      const response = await companyService.getAll({ limit: 100 });
      setCompanies(response.data);
    } catch (error) {
      console.error('Error al cargar empresas:', error);
    }
  };

  const loadRetentions = async (companyId) => {
    try {
      const response = await retentionService.getAll({ companyId, limit: 9999 });
      setRetentions(response.data);
    } catch (error) {
      console.error('Error al cargar retenciones:', error);
    }
  };

  const loadProceedings = async () => {
    try {
      setLoading(true);
      const response = await proceedingService.getAll({
        search: debouncedSearch,
        companyId: selectedCompany,
        page,
        limit: 10,
        startDate,
        endDate,
        sortBy,
        sortDir,
        ...(retentionAlert && { retentionAlert }),
      });
      setProceedings(response.data);
      setPagination(response.pagination);
    } catch (error) {
      showSnackbar('Error al cargar expedientes', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedProceeding(null);
    setOpenModal(true);
  };

  const handleEdit = (proceeding) => {
    setSelectedProceeding(proceeding);
    setOpenModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Estás seguro de eliminar este expediente?')) {
      try {
        await proceedingService.delete(id);
        setProceedings(prev => prev.filter(p => p.id !== id));
        setPagination(prev => prev ? { ...prev, total: (prev.total || 1) - 1 } : prev);
        showSnackbar('Expediente eliminado exitosamente', 'success');
      } catch (error) {
        showSnackbar('Error al eliminar expediente', 'error');
      }
    }
  };

  const handleSave = async (data) => {
    try {
      if (selectedProceeding) {
        await proceedingService.update(selectedProceeding.id, data);
        setProceedings(prev => prev.map(p =>
          p.id === selectedProceeding.id ? { ...p, ...data } : p
        ));
        showSnackbar('Expediente actualizado exitosamente', 'success');
      } else {
        const created = await proceedingService.create(data);
        showSnackbar('Expediente creado exitosamente', 'success');
        setOpenModal(false);
        navigate(`/proceedings/${created.id}`);
        return;
      }
      setOpenModal(false);
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error al guardar expediente', 'error');
      throw error;
    }
  };

  const handleSortChange = (field) => {
    if (field === sortBy) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDir('asc');
    }
    setPage(1);
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleOpenOneDrive = (mode) => {
    setOneDriveMode(mode);
    setOneDriveDialogOpen(true);
  };

  const handleCloseOneDrive = () => {
    setOneDriveDialogOpen(false);
    loadProceedings();
  };

  const handleJobClick = (job) => {
    setActiveJob(job);
    setProgressDialogOpen(true);
  };

  const handleCloseProgress = () => {
    setProgressDialogOpen(false);
    setActiveJob(null);
    loadProceedings();
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Polling: silently refresh every 30 seconds when no modal is open
  const pollingRef = useRef(null);
  useEffect(() => {
    pollingRef.current = setInterval(() => {
      if (!openModal) {
        loadProceedings();
      }
    }, 30000);
    return () => clearInterval(pollingRef.current);
  }, [debouncedSearch, selectedCompany, page, startDate, endDate, sortBy, sortDir, openModal, retentionAlert]);

  return (
    <Box>
      <PageHeader
        title="Expedientes"
        actions={
          <>
            {isOwner && <ActiveJobsBadge onJobClick={handleJobClick} />}
            {isOwner && (
              <>
                <Button variant="outlined" startIcon={<History />} onClick={() => navigate('/proceedings/import-history')} size="small">
                  Historial
                </Button>
                <Button variant="outlined" startIcon={<Cloud />} onClick={() => handleOpenOneDrive('new')} color="info" size="small">
                  OneDrive (Nuevos)
                </Button>
                <Button variant="outlined" startIcon={<Cloud />} onClick={() => handleOpenOneDrive('existing')} color="info" size="small">
                  OneDrive (Existentes)
                </Button>
              </>
            )}
            <Button variant="outlined" startIcon={<Download />} onClick={() => proceedingService.exportExcel({ search, companyId: selectedCompany, startDate, endDate })}>
              Exportar Excel
            </Button>
            {hasPermission('proceeding.create') && (
              <Button variant="contained" startIcon={<Add />} onClick={handleCreate}>
                Nuevo Expediente
              </Button>
            )}
          </>
        }
      />

      <Box sx={{ bgcolor: '#ffffff', borderRadius: '14px', p: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.06)', mb: 3 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <TextField
            fullWidth
            placeholder="Buscar por nombre o código..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        {isOwner && (
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Empresa</InputLabel>
              <Select
                value={selectedCompany}
                onChange={(e) => {
                  setSelectedCompany(e.target.value);
                  setPage(1);
                }}
                label="Empresa"
              >
                <MenuItem value="">Todas</MenuItem>
                {companies.slice().sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((company) => (
                  <MenuItem key={company.id} value={company.id}>
                    {company.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Fecha inicio"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Fecha fin"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
      </Grid>
      </Box>

      {/* Alertas de retención */}
      <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
        <Tooltip title="Expedientes cuyo tiempo en archivo local está por vencer (próximos 90 días)">
          <Chip
            icon={<SwapHoriz />}
            label="Próximos a trasladar"
            color={retentionAlert === 'transfer' ? 'warning' : 'default'}
            variant={retentionAlert === 'transfer' ? 'filled' : 'outlined'}
            onClick={() => { setRetentionAlert(prev => prev === 'transfer' ? '' : 'transfer'); setPage(1); }}
            clickable
          />
        </Tooltip>
        <Tooltip title="Expedientes cuyo tiempo total de retención (local + central) está por vencer y deben eliminarse (próximos 90 días)">
          <Chip
            icon={<Warning />}
            label="Próximos a eliminar"
            color={retentionAlert === 'elimination' ? 'error' : 'default'}
            variant={retentionAlert === 'elimination' ? 'filled' : 'outlined'}
            onClick={() => { setRetentionAlert(prev => prev === 'elimination' ? '' : 'elimination'); setPage(1); }}
            clickable
          />
        </Tooltip>
        {retentionAlert && (
          <Chip
            label="Limpiar filtro"
            variant="outlined"
            onDelete={() => { setRetentionAlert(''); setPage(1); }}
            size="small"
          />
        )}
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <LoadingLogo size={120} />
        </Box>
      ) : (
        <ProceedingTable
          proceedings={proceedings}
          onEdit={handleEdit}
          onDelete={handleDelete}
          page={page}
          onPageChange={setPage}
          pagination={pagination}
          canView={hasPermission('proceeding.view')}
          canEdit={hasPermission('proceeding.update')}
          canDelete={hasPermission('proceeding.delete')}
          sortBy={sortBy}
          sortDir={sortDir}
          onSortChange={handleSortChange}
        />
      )}

      <ProceedingModalForm
        open={openModal}
        onClose={() => setOpenModal(false)}
        onSave={handleSave}
        proceeding={selectedProceeding}
        companies={companies}
        retentions={retentions}
        onCompanyChange={loadRetentions}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {isOwner && (
        <OneDriveBulkImport
          open={oneDriveDialogOpen}
          onClose={handleCloseOneDrive}
          companies={companies}
          mode={oneDriveMode}
        />
      )}

      {activeJob && (
        <Dialog open={progressDialogOpen} onClose={handleCloseProgress} maxWidth="md" fullWidth>
          <DialogTitle>Progreso de Importación</DialogTitle>
          <DialogContent>
            <BulkImportProgress jobId={activeJob.id} onClose={handleCloseProgress} />
          </DialogContent>
        </Dialog>
      )}
    </Box>
  );
};

export default ProceedingList;
